import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent,
  Stack, SvgIcon, TextField, Typography, useTheme,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import LocalOffer from '@mui/icons-material/LocalOffer';
import CheckCircle from '@mui/icons-material/CheckCircle';
import EventAvailable from '@mui/icons-material/EventAvailable';
import Timer from '@mui/icons-material/Timer';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Trash01 } from '../../../../assets';
import { promotionsClient, PromotionDto } from '../../../../app/services/api/promotions';
import { PromotionStatusType } from '../../../../app/services/api/promotions/promotionTypes';
import { client } from '../../../../app/services/api/orderManagementClient';
import { applyRoundAndFormat } from '../../../../utils';
import useLogError from '../../../../hooks/useLogError';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import { ConfirmationDialog } from '../../../../components/shared/dialogs/ConfirmationDialog';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { UserContext } from '../../../../components/shared/useUser';
import { TableColumn, TableSectionProps } from '../../../../components/shared/tables/TableSection';
import { InsightTile } from '../../../../components/shared/tiles/InsightTile';
import { QuickFilterBar, QuickFilterGroup } from '../../../../components/shared/filters/QuickFilterBar';
import type { ListPageHeadline } from '../../../../components/page-templates/list-page/ListPageTemplate';
import { useInstanceMarket } from '../../../../contexts/InstanceMarketContext';

type StatusFilter = '' | PromotionStatusType;
interface CatalogueOption { id: number; name: string; }

const PAGE_SIZE = 10;
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

type Props = {
  onFilterChange?: (_instance: string, _market: string, filters?: { status?: string; catalogueId?: string; searchTerm?: string }) => void;
  initialInstance?: string;
  initialMarket?: string;
  initialStatus?: string;
  initialCatalogue?: string;
  initialSearchTerm?: string;
  /** Splits filters, tiles and table so the caller can slot them into ListPageTemplate. */
  children: (parts: {
    filterBar: ReactNode;
    quickFilterContent: ReactNode;
    advancedFilterContent: ReactNode;
    advancedFilterActions: ReactNode;
    headlineContent: ListPageHeadline;
    table: TableSectionProps<PromotionDto>;
    isInitialLoading: boolean;
  }) => ReactNode;
};

const PromotionsListPageContent = ({
  onFilterChange, initialInstance, initialMarket, initialStatus, initialCatalogue, initialSearchTerm, children,
}: Props) => {
  const { t } = useTranslation('promotions');
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermissionTo } = useContext(UserContext);
  const { logError } = useLogError();
  const canWritePromotions = hasPermissionTo(['PromotionWrite', 'PromotionAdmin']);
  const canDeletePromotions = hasPermissionTo(['PromotionDelete']);

  const { instances, getMarkets, ensureMarketsLoaded, isMarketsLoading } = useInstanceMarket();

  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [selectedMarket, setSelectedMarket] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<StatusFilter>('');
  const [selectedCatalogue, setSelectedCatalogue] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(0);

  const [allPromotions, setAllPromotions] = useState<PromotionDto[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false);
  const [catalogues, setCatalogues] = useState<CatalogueOption[]>([]);

  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<PromotionDto | null>(null);
  const [feedback, setFeedback] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const markets = getMarkets(selectedInstance);

  useEffect(() => {
    if (initialInstance) setSelectedInstance(initialInstance);
    if (initialMarket) setSelectedMarket(initialMarket);
    if (initialStatus !== undefined) setSelectedStatus(initialStatus as StatusFilter);
    if (initialCatalogue !== undefined) setSelectedCatalogue(initialCatalogue);
    if (initialSearchTerm !== undefined) {
      setSearchInput(initialSearchTerm);
      setSearchText(initialSearchTerm);
    }
  }, [initialInstance, initialMarket, initialStatus, initialCatalogue, initialSearchTerm]);

  useEffect(() => {
    if (selectedInstance) {
      ensureMarketsLoaded(selectedInstance);
    } else {
      setSelectedMarket('');
    }
  }, [selectedInstance, ensureMarketsLoaded]);

  // Auto-select the only available market, matching the fallback used by Suppliers/Products.
  useEffect(() => {
    if (isMarketsLoading(selectedInstance)) return;
    if (markets.length === 1) {
      const onlyMarket = markets[0].customerTypeId.toString();
      if (selectedMarket !== onlyMarket) {
        setSelectedMarket(onlyMarket);
        onFilterChange?.(selectedInstance, onlyMarket, { status: selectedStatus, catalogueId: selectedCatalogue, searchTerm: searchText });
      }
    }
  }, [markets, isMarketsLoading, selectedInstance]);

  useEffect(() => {
    if (!selectedInstance || !selectedMarket) {
      setAllPromotions([]);
      return;
    }
    let isActive = true;
    setIsLoading(true);
    promotionsClient.getPromotions({
      companyId: Number(selectedInstance),
      customerTypeId: Number(selectedMarket),
      pageSize: 2147483647,
    })
      .then((data) => { if (isActive) setAllPromotions(data); })
      .catch((error) => {
        if (isActive) {
          logError('Failed to load promotions');
          setFeedback({ message: t('loadErrorMessage'), severity: 'error' });
        }
      })
      .finally(() => { if (isActive) { setIsLoading(false); setHasLoadedOnce(true); } });
    return () => { isActive = false; };
  }, [selectedInstance, selectedMarket]);

  useEffect(() => {
    if (!selectedInstance || !selectedMarket) { setCatalogues([]); return; }
    let isActive = true;
    client.getMarketCatalogues({
      companyId: selectedInstance, customerTypeId: selectedMarket,
      searchTerm: '', sort: 'name', direction: 'asc', pageNumber: 0, pageSize: 2147483647,
    })
      .then((result) => {
        if (isActive) setCatalogues((result.data?.items ?? []).map((c) => ({ id: c.customerGroupId, name: c.name })));
      })
      .catch(() => {});
    return () => { isActive = false; };
  }, [selectedInstance, selectedMarket]);

  const filteredPromotions = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return allPromotions.filter((promo) => {
      if (selectedStatus && promo.status !== selectedStatus) return false;
      if (selectedCatalogue && promo.catalogueId !== Number(selectedCatalogue)) return false;
      if (search && !promo.name.toLowerCase().includes(search)) return false;
      return true;
    });
  }, [allPromotions, searchText, selectedStatus, selectedCatalogue]);

  const maxPages = Math.max(Math.ceil(filteredPromotions.length / PAGE_SIZE), 1);
  const visiblePromotions = useMemo(
    () => filteredPromotions.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE),
    [filteredPromotions, currentPage],
  );

  const totalCount = allPromotions.length;
  const activeCount = useMemo(() => allPromotions.filter((p) => p.status === 'Active').length, [allPromotions]);
  const scheduledCount = useMemo(() => allPromotions.filter((p) => p.status === 'Scheduled').length, [allPromotions]);
  const endingSoonCount = useMemo(() => {
    const now = Date.now();
    return allPromotions.filter((p) => {
      if (p.status !== 'Active') return false;
      const end = new Date(p.endDateUtc).getTime();
      return end >= now && end - now <= SEVEN_DAYS_MS;
    }).length;
  }, [allPromotions]);

  const marketCurrencyCode = markets.find((m) => m.customerTypeId.toString() === selectedMarket)?.currencyCode;

  const formatPricingDisplay = (promo: PromotionDto) => {
    if (promo.pricingType === 1) return `${promo.discountPercentage}% ${t('pricingTypePercentage').toLowerCase()}`;
    return applyRoundAndFormat(promo.fixedPromoUnitPrice ?? 0, marketCurrencyCode);
  };

  const statusLabelMap: Record<PromotionStatusType, string> = {
    Active: t('statusActive'),
    Scheduled: t('statusScheduled'),
    Ended: t('statusEnded'),
    Deactivated: t('statusDeactivated'),
  };
  const statusColorMap: Record<PromotionStatusType, 'success' | 'warning' | 'default'> = {
    Active: 'success',
    Scheduled: 'warning',
    Ended: 'default',
    Deactivated: 'default',
  };

  const applyFilters = (filters?: { status?: string; catalogueId?: string; searchTerm?: string }) => {
    onFilterChange?.(selectedInstance, selectedMarket, filters);
  };

  const handleInstanceChange = (event: SelectChangeEvent) => {
    setSelectedInstance(event.target.value);
    setSelectedMarket('');
    setCurrentPage(0);
    onFilterChange?.(event.target.value, '');
  };

  const handleMarketChange = (event: SelectChangeEvent) => {
    setSelectedMarket(event.target.value);
    setCurrentPage(0);
    onFilterChange?.(selectedInstance, event.target.value);
  };

  const handleSearch = () => {
    setSearchText(searchInput);
    setCurrentPage(0);
    applyFilters({ searchTerm: searchInput });
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchText('');
    setSelectedStatus('');
    setSelectedCatalogue('');
    setCurrentPage(0);
    applyFilters({ status: '', catalogueId: '', searchTerm: '' });
  };

  const handleStatusFilterChange = (value: readonly string[]) => {
    const nextStatus = (value[0] as StatusFilter) ?? '';
    setSelectedStatus(nextStatus);
    setCurrentPage(0);
    applyFilters({ status: nextStatus });
  };

  const handleCatalogueChange = (event: SelectChangeEvent) => {
    setSelectedCatalogue(event.target.value);
    setCurrentPage(0);
    applyFilters({ catalogueId: event.target.value });
  };

  const handleClearAdvancedFilters = () => {
    setSelectedCatalogue('');
    setSelectedStatus('');
    setCurrentPage(0);
    applyFilters({ catalogueId: '', status: '' });
  };

  const handleRowClick = (promo: PromotionDto) => {
    if (!canWritePromotions) return;
    navigate(`/procurement/promotions/edit/${promo.id}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`);
  };

  const handleDelete = async () => {
    if (!deleteCandidate) return;
    setIsDeleting(true);
    try {
      await promotionsClient.deletePromotion(deleteCandidate.id);
      setFeedback({ message: t('deleteSuccessMessage'), severity: 'success' });
      setAllPromotions((prev) => prev.filter((p) => p.id !== deleteCandidate.id));
      setDeleteCandidate(null);
    } catch (error) {
      logError('Failed to delete promotion');
      setFeedback({ message: t('deleteErrorMessage'), severity: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: TableColumn<PromotionDto>[] = [
    {
      id: 'name',
      label: t('name'),
      render: (row) => (
        <Typography variant='textSM'
          fontWeight={500}>{row.name}</Typography>
      ),
    },
    { id: 'assignedProducts', label: t('assignedProducts'), field: 'assignedProductCount' },
    { id: 'pricingType', label: t('pricingType'), render: (row) => formatPricingDisplay(row) },
    { id: 'claimed', label: t('claimed'), render: (row) => `${row.claimedQuantity} / ${row.quantityCap}` },
    {
      id: 'status',
      label: t('status'),
      render: (row) => (
        <Chip label={statusLabelMap[row.status]}
          color={statusColorMap[row.status]}
          size='small' />
      ),
    },
    { id: 'endDate', label: t('endDate'), render: (row) => new Date(row.endDateUtc).toLocaleDateString() },
  ];

  if (canDeletePromotions) {
    columns.push({
      id: 'actions',
      label: t('actions'),
      render: (row) => (
        <IconButton
          aria-label={t('delete')}
          size='small'
          onClick={(event) => { event.stopPropagation(); setDeleteCandidate(row); }}
        >
          <SvgIcon style={{ color: theme.palette.error[500] }}><Trash01 /></SvgIcon>
        </IconButton>
      ),
    });
  }

  const table: TableSectionProps<PromotionDto> = {
    title: t('pageTitle'),
    columns,
    rows: visiblePromotions,
    getRowId: (row) => row.id,
    onRowClick: canWritePromotions ? handleRowClick : undefined,
    recordCount: filteredPromotions.length,
    recordCountLabel: t('pageTitle').toLowerCase(),
    loading: isLoading || !hasLoadedOnce,
    emptyContent: (
      <EmptyTable
        bodyRowHeight={56}
        headerHeight={45}
        text={t('pageTitle')}
        searchTerm={searchInput}
        onClear={handleClear}
        onAdd={() => navigate('/procurement/promotions/create')}
        onAddIsCreating={false}
        showOnAdd={canWritePromotions}
        addItemText={t('createPromotion')}
        withPaginationShown={false}
      />
    ),
    page: currentPage,
    maxPages,
    onPageChange: setCurrentPage,
  };

  const filterBar = (
    <Grid container
      spacing={5}>
      <Grid item
        lg={3}
        sm={6}
        xs={12}>
        <TextField
          fullWidth
          label={t('search')}
          placeholder={t('searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
        />
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button fullWidth
          variant='secondary'
          size='lg'
          onClick={handleClear}>
          {t('clear')}
        </Button>
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          variant='primary'
          size='lg'
          onClick={handleSearch}
          startIcon={<SvgIcon><Search /></SvgIcon>}
        >
          {t('search')}
        </Button>
      </Grid>
      <Grid item
        lg={2.5}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>{t('filterByInstance')}</InputLabel>
          <Select
            value={selectedInstance}
            onChange={handleInstanceChange}
            label={t('filterByInstance')}
            displayEmpty
            notched
          >
            <MenuItem value=''><em>{t('selectInstance')}</em></MenuItem>
            {instances.filter((i) => i.id != null).map((inst) => (
              <MenuItem key={inst.id}
                value={inst.id.toString()}>{inst.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        lg={2.5}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>{t('filterByMarket')}</InputLabel>
          <Select
            value={selectedMarket}
            onChange={handleMarketChange}
            label={t('filterByMarket')}
            displayEmpty
            notched
            disabled={!selectedInstance}
          >
            <MenuItem value=''><em>{t('selectMarket')}</em></MenuItem>
            {markets.map((m) => (
              <MenuItem key={m.customerTypeId}
                value={m.customerTypeId.toString()}>{m.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  const statusQuickFilterGroup: QuickFilterGroup = {
    id: 'status',
    options: [
      { id: '', label: t('allStatuses') },
      ...(['Active', 'Scheduled', 'Ended', 'Deactivated'] as PromotionStatusType[])
        .map((status) => ({ id: status, label: statusLabelMap[status] })),
    ],
    value: [selectedStatus || ''],
    multiple: false,
    onChange: handleStatusFilterChange,
  };

  const catalogueQuickFilterGroup: QuickFilterGroup = {
    id: 'catalogue',
    options: [
      { id: '', label: t('allCatalogues') },
      ...catalogues.map((c) => ({ id: c.id.toString(), label: c.name })),
    ],
    value: [selectedCatalogue || ''],
    multiple: false,
    onChange: (value) => {
      const nextCatalogue = value[0] ?? '';
      setSelectedCatalogue(nextCatalogue);
      setCurrentPage(0);
      applyFilters({ catalogueId: nextCatalogue });
    },
  };

  const quickFilterSectionHeaderSx = { mb: 2, fontWeight: 600 } as const;

  const quickFilterContent = (
    <Stack spacing={4}>
      <Box>
        <Typography variant="textSM"
          sx={quickFilterSectionHeaderSx}>
          {t('status')}
        </Typography>
        <QuickFilterBar groups={[statusQuickFilterGroup]} />
      </Box>
      {catalogues.length > 0 && (
        <Box>
          <Typography variant="textSM"
            sx={quickFilterSectionHeaderSx}>
            {t('catalogue')}
          </Typography>
          <QuickFilterBar groups={[catalogueQuickFilterGroup]} />
        </Box>
      )}
    </Stack>
  );

  const handleStatusSelectChange = (event: SelectChangeEvent) => {
    handleStatusFilterChange(event.target.value ? [event.target.value] : []);
  };

  const advancedFilterContent = (
    <Grid container
      spacing={5}>
      <Grid item
        lg={4}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>{t('filterByStatus')}</InputLabel>
          <Select
            value={selectedStatus}
            onChange={handleStatusSelectChange}
            label={t('filterByStatus')}
            displayEmpty
            notched
          >
            <MenuItem value=''>{t('allStatuses')}</MenuItem>
            {(['Active', 'Scheduled', 'Ended', 'Deactivated'] as PromotionStatusType[]).map((status) => (
              <MenuItem key={status}
                value={status}>{statusLabelMap[status]}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        lg={4}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>{t('filterByCatalogue')}</InputLabel>
          <Select
            value={selectedCatalogue}
            onChange={handleCatalogueChange}
            label={t('filterByCatalogue')}
            displayEmpty
            notched
          >
            <MenuItem value=''>{t('allCatalogues')}</MenuItem>
            {catalogues.map((c) => (
              <MenuItem key={c.id}
                value={c.id.toString()}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  const advancedFilterActions = (
    <Button
      variant='secondary'
      size='md'
      onClick={handleClearAdvancedFilters}
    >
      {t('clear')}
    </Button>
  );

  const headlineContent: ListPageHeadline = {
    insights: [
      <InsightTile
        key='total'
        label={t('procurementTileTotalPromotions', 'Total Promotions')}
        value={totalCount}
        description={t('procurementTileTotalPromotionsDescription', 'All promotions in this instance/market')}
        icon={<LocalOffer />}
        variant='primary'
        loading={isLoading && !hasLoadedOnce}
      />,
      <InsightTile
        key='active'
        label={t('procurementTileActivePromotions', 'Active Promotions')}
        value={activeCount}
        description={t('procurementTileActivePromotionsDescription', 'Currently running promotions')}
        icon={<CheckCircle />}
        variant='success'
        loading={isLoading && !hasLoadedOnce}
      />,
      <InsightTile
        key='scheduled'
        label={t('procurementTileScheduledPromotions', 'Scheduled Promotions')}
        value={scheduledCount}
        description={t('procurementTileScheduledPromotionsDescription', 'Promotions starting in the future')}
        icon={<EventAvailable />}
        variant='secondary'
        loading={isLoading && !hasLoadedOnce}
      />,
      <InsightTile
        key='endingSoon'
        label={t('procurementTileEndingSoon', 'Ending Soon (7 Days)')}
        value={endingSoonCount}
        description={t('procurementTileEndingSoonDescription', 'Active promotions ending within 7 days')}
        icon={<Timer />}
        variant='warning'
        loading={isLoading && !hasLoadedOnce}
      />,
    ],
  };

  return (
    <>
      {children({
        filterBar,
        quickFilterContent,
        advancedFilterContent,
        advancedFilterActions,
        headlineContent,
        table,
        isInitialLoading: isLoading && !hasLoadedOnce,
      })}

      <ConfirmationDialog
        open={Boolean(deleteCandidate)}
        icon={<Trash01 />}
        variant='bad'
        title={t('deleteConfirmTitle')}
        message={t('deleteConfirmMessage', { name: deleteCandidate?.name })}
        confirmButtonText={isDeleting ? t('deleting') : t('delete')}
        cancelButtonText={t('cancel')}
        isProcessing={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteCandidate(null)}
      />

      {feedback && (
        <MessagesSnackbar
          open={Boolean(feedback)}
          onClose={() => setFeedback(null)}
          message={feedback.message}
          severity={feedback.severity}
          duration={4000}
        />
      )}
    </>
  );
};

export default PromotionsListPageContent;
